/**
 * CommandPalette — Phase 39 activation entrypoint.
 *
 * VS Code-style DOM overlay opened when user types "cnf" on the homepage.
 * Three fixed commands: start game, open secret, who made this.
 * Substring filter, arrow key navigation, Enter to confirm, Escape to dismiss.
 *
 * NOT rendered on touch devices (matchMedia hover:none + pointer:coarse).
 *
 * Keyboard events captured via a window keydown listener (not a real input element)
 * so the prompt can show a styled terminal cursor. SAFE-02: modifier-held keys are
 * ignored for filter updates so browser shortcuts (Cmd+R, Ctrl+C) are unaffected.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TOUCH_MEDIA_QUERY = '(hover: none) and (pointer: coarse)';

type CommandId = 'start-game' | 'open-secret' | 'who-made-this';

interface Command {
  id: CommandId;
  label: string;
}

const COMMANDS: readonly Command[] = [
  { id: 'start-game', label: 'start game' },
  { id: 'open-secret', label: 'open secret' },
  { id: 'who-made-this', label: 'who made this' },
] as const;

const WHO_MADE_THIS_RESPONSE =
  'christopher fabian. he also made you read this. (press Escape to go back)';

export interface CommandPaletteProps {
  onClose: () => void;
  onActivate: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ onClose, onActivate }) => {
  const [filterText, setFilterText] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [showEasterResponse, setShowEasterResponse] = useState<boolean>(false);
  const navigate = useNavigate();

  const isTouchDevice =
    typeof window !== 'undefined' && window.matchMedia(TOUCH_MEDIA_QUERY).matches;

  const filtered = COMMANDS.filter((c) =>
    c.label.toLowerCase().includes(filterText.toLowerCase())
  );

  const confirmCommand = useCallback(
    (cmd: Command): void => {
      if (cmd.id === 'start-game') {
        onActivate();
        onClose();
      } else if (cmd.id === 'open-secret') {
        onClose();
        navigate('/secret');
      } else if (cmd.id === 'who-made-this') {
        setShowEasterResponse(true);
      }
    },
    [navigate, onActivate, onClose]
  );

  useEffect(() => {
    if (isTouchDevice) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      // Escape always dismisses (or clears easter response first)
      if (e.key === 'Escape') {
        e.preventDefault();
        if (showEasterResponse) {
          setShowEasterResponse(false);
          setFilterText('');
          setSelectedIndex(0);
          return;
        }
        onClose();
        return;
      }

      // Block everything else while easter response is showing
      if (showEasterResponse) return;

      // SAFE-02: modifier keys skip filter updates (browser shortcuts preserved)
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        if (filtered.length > 0 && selectedIndex < filtered.length) {
          confirmCommand(filtered[selectedIndex]);
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        setFilterText((t) => t.slice(0, -1));
        setSelectedIndex(0);
        return;
      }

      // Single printable character appends to filter
      if (e.key.length === 1) {
        e.preventDefault();
        setFilterText((t) => t + e.key.toLowerCase());
        setSelectedIndex(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [confirmCommand, filtered, selectedIndex, showEasterResponse, onClose, isTouchDevice]);

  if (isTouchDevice) return null;

  const renderHighlightedLabel = (label: string): React.ReactNode => {
    if (!filterText) return label;
    const lower = label.toLowerCase();
    const idx = lower.indexOf(filterText.toLowerCase());
    if (idx === -1) return label;
    return (
      <>
        {label.slice(0, idx)}
        <span className="command-palette__command__match">
          {label.slice(idx, idx + filterText.length)}
        </span>
        {label.slice(idx + filterText.length)}
      </>
    );
  };

  return (
    <div
      className="command-palette-backdrop"
      role="dialog"
      aria-label="Command palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="command-palette">
        <div className="command-palette__prompt">
          <span className="command-palette__filter-display">{filterText}</span>
        </div>
        {showEasterResponse ? (
          <div className="command-palette__easter">{WHO_MADE_THIS_RESPONSE}</div>
        ) : (
          <ul className="command-palette__command-list">
            {filtered.length === 0 ? (
              <li className="command-palette__command command-palette__command--empty">
                no matches
              </li>
            ) : (
              filtered.map((cmd, i) => (
                <li
                  key={cmd.id}
                  className={
                    'command-palette__command' +
                    (i === selectedIndex ? ' command-palette__command--selected' : '')
                  }
                >
                  {renderHighlightedLabel(cmd.label)}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
